(function() {
  if (!globalThis.AlgoliaConfig) {
    return;
  }

  const config = globalThis.AlgoliaConfig;

  let articlesIndex = null;
  let pagesIndex = null;
  let initialized = false;
  let selectedFacets = {};
  let searchInput = null;
  let sidebar = null;
  let mainContent = null;
  let paginationContainer = null;
  let resultsCountEl = null;
  let mobileFilterToggleButton = null;
  let currentSortBy = 'featured';
  let latestDisplayRequestId = 0;
  let shouldUsePagesIndex = true;
  let isCustomBlogContext = false;
  const ITEMS_PER_PAGE = 6;

  const facetDisplayNames = {
     'meta.custom.by_concern': 'By Concern',
    'meta.custom.by_category': 'By Category'
  };

  function createElement(tag, className, textContent = '') {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (textContent) el.textContent = textContent;
    return el;
  }

  function resetFiltersAndPage() {
    selectedFacets = {};
  }

  function updateAlgoliaIndex(indexName) {
    const client = algoliasearch(config.appId, config.apiKey);
    articlesIndex = client.initIndex(indexName);

    if (shouldUsePagesIndex && config.pagesIndices?.featured) {
      pagesIndex = client.initIndex(config.pagesIndices.featured);
    } else {
      pagesIndex = null;
    }
  }

  function toggleFacet(facetName, facetValue) {
    if (!selectedFacets[facetName]) {
      selectedFacets[facetName] = [];
    }

    const index = selectedFacets[facetName].indexOf(facetValue);
    if (index > -1) {
      selectedFacets[facetName].splice(index, 1);
      if (selectedFacets[facetName].length === 0) {
        delete selectedFacets[facetName];
      }
    } else {
      selectedFacets[facetName].push(facetValue);
    }

    displayArticles();
  }

  function buildFacetFilters() {
    const filters = [];
    Object.keys(selectedFacets).forEach(facet => {
      selectedFacets[facet].forEach(value => {
        filters.push(`${facet}:${value}`);
      });
    });
    return filters;
  }

  function isPageLikeHit(hit) {
    const explicitType = String(
      hit?.type || hit?.object_type || hit?.record_type || hit?.content_type || ''
    ).toLowerCase();

    if (explicitType === 'page' || explicitType === 'pages') {
      return true;
    }

    const url = String(hit?.url || hit?.full_url || hit?.link || '').toLowerCase();
    if (url.includes('/pages/')) {
      return true;
    }

    const objectId = String(hit?.objectID || '').toLowerCase();
    if (objectId.includes('/pages/') || objectId.startsWith('page_')) {
      return true;
    }

    const handle = String(hit?.handle || '').toLowerCase();
    if (handle.startsWith('pages/')) {
      return true;
    }

    return false;
  }

  function getTrendingArticleAttributes() {
    return ['title', 'handle', 'image', 'excerpt', 'body_html', 'short_description', 'meta.custom.short_description', 'published_at'];
  }

  function createDefaultTrendingSection(parent) {
    if (!parent || parent.querySelector('.trending-articles-section')) {
      return null;
    }

    const divider = createElement('div', 'trending-articles-divider');
    const section = createElement('div', 'trending-articles-section');
    section.dataset.trendingLimit = '4';
    section.dataset.trendingManual = '[]';

    const textWrapper = createElement('div', 'articlelist-text-wrapper');

    const headingRow = createElement('div', 'trending-heading-row');
    headingRow.appendChild(createElement('h2', 'articlelist-heading', 'TRENDING'));

    const arrowsContainer = createElement('div', 'trending-arrows-container');

    const leftArrow = createElement('button', 'trending-arrow trending-arrow--left');
    leftArrow.type = 'button';
    leftArrow.setAttribute('aria-label', 'Previous slide');

    const rightArrow = createElement('button', 'trending-arrow trending-arrow--right');
    rightArrow.type = 'button';
    rightArrow.setAttribute('aria-label', 'Next slide');

    arrowsContainer.appendChild(leftArrow);
    arrowsContainer.appendChild(rightArrow);
    headingRow.appendChild(arrowsContainer);

    const headerControls = createElement('div', 'trending-header-controls');
    headerControls.appendChild(createElement('h3', 'articlelist-sub-heading', 'Most Read'));

    const trendingList = createElement('ul', 'trending-articles-list');
    trendingList.style.margin = '0';
    trendingList.style.padding = '0';

    textWrapper.appendChild(headingRow);
    textWrapper.appendChild(headerControls);
    section.appendChild(textWrapper);
    section.appendChild(trendingList);

    parent.appendChild(divider);
    parent.appendChild(section);

    return {
      trendingSection: section,
      trendingContainer: trendingList,
      trendingList: trendingList,
      leftArrow: leftArrow,
      rightArrow: rightArrow
    };
  }

  function resolveTrendingElements(contextPanel) {
    if (contextPanel) {
      const inPanelSection = contextPanel.querySelector('.trending-articles-section');
      const inPanelContainer = contextPanel.querySelector('.trending-articles-list, .trending-articles-container');
      if (inPanelSection && inPanelContainer) {
        return {
          trendingSection: inPanelSection,
          trendingContainer: inPanelContainer
        };
      }

      const parent = contextPanel.parentElement;
      if (parent) {
        const siblingSection = parent.querySelector('.trending-articles-section');
        const siblingContainer = parent.querySelector('.trending-articles-list, .trending-articles-container');
        if (siblingSection && siblingContainer) {
          return {
            trendingSection: siblingSection,
            trendingContainer: siblingContainer
          };
        }
      }

      if (isCustomBlogContext) {
        return null;
      }

      return createDefaultTrendingSection(contextPanel);
    }

    const globalSection = document.querySelector('.trending-articles-section');
    const globalContainer = document.querySelector('.trending-articles-list, .trending-articles-container');
    if (globalSection && globalContainer) {
      return {
        trendingSection: globalSection,
        trendingContainer: globalContainer
      };
    }

    return null;
  }

  function getManualTrendingSelection(trendingSection) {
    const raw = trendingSection?.dataset.trendingManual || '[]';

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((item) => {
          if (!item || typeof item !== 'object') {
            return null;
          }

          return {
            id: item.id ? String(item.id).trim() : '',
            handle: item.handle ? String(item.handle).trim() : ''
          };
        })
        .filter((item) => item && (item.id || item.handle));
    } catch (error) {
      console.warn('[Algolia] Failed to parse manual trending selection:', error);
      return [];
    }
  }

  function matchesManualSelection(hit, selection) {
    const hitHandle = hit?.handle ? String(hit.handle).trim() : '';
    const hitId = hit?.id ? String(hit.id).trim() : '';
    const hitObjectId = hit?.objectID ? String(hit.objectID).trim() : '';

    if (selection.handle && hitHandle === selection.handle) {
      return true;
    }

    if (selection.id) {
      if (hitId === selection.id) {
        return true;
      }

        if (hitObjectId === selection.id || hitObjectId.endsWith(`/${selection.id}`)) {
        return true;
      }
    }

    return false;
  }

  async function fetchTrendingFromManualSelection(selections, limit) {
    const dedupedSelections = [];
    const seenKeys = new Set();

    selections.forEach((selection) => {
      const key = selection.handle ? `handle:${selection.handle}` : `id:${selection.id}`;
      if (!seenKeys.has(key)) {
        dedupedSelections.push(selection);
        seenKeys.add(key);
      }
    });

    const constrainedSelections = dedupedSelections.slice(0, limit);

    if (!constrainedSelections.length) {
      return [];
    }

    const byKey = new Map();
    const keyForSelection = (selection) => (selection.handle ? `handle:${selection.handle}` : `id:${selection.id}`);
    const pendingKeys = new Set(constrainedSelections.map((selection) => keyForSelection(selection)));

    const attributes = getTrendingArticleAttributes();
    const hitsPerPage = 100;
    const maxPagesToScan = 20;

    for (let page = 0; page < maxPagesToScan && pendingKeys.size > 0; page += 1) {
      let pageResults;
      try {
        pageResults = await articlesIndex.search('', {
          page,
          hitsPerPage,
          attributesToRetrieve: attributes
        });
      } catch (error) {
        console.warn('[algolia-articles-search] Algolia search failed:', error);
        break;
      }

      const hits = pageResults?.hits || [];
      if (!hits.length) {
        break;
      }

      constrainedSelections.forEach((selection) => {
        const key = keyForSelection(selection);
        if (!pendingKeys.has(key)) {
          return;
        }

        const match = hits.find((hit) => matchesManualSelection(hit, selection));
        if (match) {
          byKey.set(key, match);
          pendingKeys.delete(key);
        }
      });

      if (page >= (pageResults.nbPages || 1) - 1) {
        break;
      }
    }

    return constrainedSelections
      .map((selection) => byKey.get(keyForSelection(selection)))
      .filter(Boolean);
  }

  async function displayTrendingArticles(contextPanel) {
    if (!articlesIndex) {
      return;
    }

    const trendingElements = resolveTrendingElements(contextPanel);
    if (!trendingElements) {
      return;
    }

    const { trendingSection, trendingContainer } = trendingElements;
    const limit = Number.parseInt(trendingSection.dataset.trendingLimit) || 4;
    const manualSelection = getManualTrendingSelection(trendingSection);

    try {
      let trendingArticles = [];

      if (manualSelection.length > 0) {
        trendingArticles = await fetchTrendingFromManualSelection(manualSelection, limit);
      }

      if (trendingArticles.length < limit) {
        const fallbackResults = await articlesIndex.search('', {
          hitsPerPage: limit,
          attributesToRetrieve: getTrendingArticleAttributes()
        });

        const existingHandles = new Set(trendingArticles.map((article) => article.handle));

        fallbackResults.hits.forEach((hit) => {
          if (trendingArticles.length < limit && !existingHandles.has(hit.handle)) {
            trendingArticles.push(hit);
            existingHandles.add(hit.handle);
          }
        });
      }

      if (trendingArticles.length > 0) {
        renderTrendingArticleCards(trendingArticles, trendingContainer);
        setTimeout(() => initializeCarousel('.trending-articles-list'), 50);
      }
    } catch (error) {
      console.error('Error fetching trending articles:', error);
    }
  }

  function initializeCarousel(selector) {
    const list = document.querySelector(selector);
    if (!list) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'trending-carousel-wrapper';
    wrapper.style.cssText = `
      overflow-x: auto;
      overflow-y: hidden;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
      width: 100%;
    `;

    const items = Array.from(list.querySelectorAll('.slick-slide'));
    items.forEach(item => wrapper.appendChild(item));

    const applySlideWidths = () => {
      const isMobile = globalThis.matchMedia('(max-width: 767px)').matches;

      wrapper.style.display = 'flex';
      wrapper.style.gap = '16px';
      wrapper.style.scrollSnapType = 'x mandatory';

      items.forEach((item, index) => {
        if (isMobile) {
          item.style.flex = '0 0 calc((100% - 16px) / 1.5)';
          item.style.minWidth = 'calc((100% - 16px) / 1.5)';
        } else {
          item.style.flex = '0 0 calc(25% - 12px)';
          item.style.minWidth = 'calc(25% - 12px)';
        }

        item.style.scrollSnapAlign = index === items.length - 1 ? 'end' : 'start';
      });
    };

    applySlideWidths();

    list.innerHTML = '';
    list.style.cssText = `
      display: flex;
      width: 100%;
      align-items: flex-start;
    `;
    list.appendChild(wrapper);

    const section = list.closest('.trending-articles-section');
    const leftArrow = section?.querySelector('.trending-arrow--left');
    const rightArrow = section?.querySelector('.trending-arrow--right');

    if (!leftArrow || !rightArrow) return;

    const getGap = () => {
      const styles = globalThis.getComputedStyle(wrapper);
      return Number.parseFloat(styles.columnGap || styles.gap || '16') || 16;
    };

    const getStepAmount = () => {
      if (!items.length) return wrapper.clientWidth;
      return items[0].getBoundingClientRect().width + getGap();
    };

    const getMaxScroll = () => Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);

    const scrollToPosition = (position) => {
      wrapper.scrollTo({ left: Math.min(getMaxScroll(), Math.max(0, position)), behavior: 'smooth' });
    };

    leftArrow.addEventListener('click', () => {
      scrollToPosition(wrapper.scrollLeft - getStepAmount());
    });

    rightArrow.addEventListener('click', () => {
      const maxScroll = getMaxScroll();
      const nextPosition = wrapper.scrollLeft + getStepAmount();
      const shouldSnapToEnd = nextPosition >= maxScroll - getStepAmount() / 2;
      scrollToPosition(shouldSnapToEnd ? maxScroll : nextPosition);
    });

    const updateArrowStates = () => {
      const isAtStart = wrapper.scrollLeft <= 0;
      const isAtEnd = wrapper.scrollLeft >= wrapper.scrollWidth - wrapper.clientWidth - 10;

      leftArrow.disabled = isAtStart;
      rightArrow.disabled = isAtEnd;

      leftArrow.style.opacity = isAtStart ? '0.5' : '1';
      rightArrow.style.opacity = isAtEnd ? '0.5' : '1';
    };

    wrapper.addEventListener('scroll', updateArrowStates);
    globalThis.addEventListener('resize', () => {
      applySlideWidths();
      updateArrowStates();
    });
    updateArrowStates();
  }

  function init() {
    if (initialized || !globalThis.algoliasearch) {
      return;
    }
    initialized = true;

    const articlesPanel = document.querySelector('[data-panel="articles"]');
    if (!articlesPanel) {
      return;
    }

    isCustomBlogContext = Boolean(articlesPanel.closest('.custom-blog-articles-listing'));
    shouldUsePagesIndex = !articlesPanel.closest('.custom-blog-articles-listing');
    updateAlgoliaIndex(config.indices.featured);

    createPanelLayout(articlesPanel);
    displayArticles();
    displayTrendingArticles(articlesPanel);
  }

  function createPanelLayout(panel) {
    if (panel.querySelector('#articles-facets-sidebar')) {
      return;
    }

    panel.innerHTML = '';

    const wrapper = createElement('div', 'articles-wrapper');

    mainContent = createElement('div', 'articles-main-content');
    mainContent.id = 'articles-main-content';
    wrapper.appendChild(mainContent);

    const controlsContainer = createElement('div', 'articles-controls-container');
    initSearchInput(controlsContainer);
    mainContent.appendChild(controlsContainer);

    sidebar = createElement('div', 'articles-sidebar');
    sidebar.id = 'articles-facets-sidebar';
    mainContent.appendChild(sidebar);

    if (!isCustomBlogContext) {
      mobileFilterToggleButton = createElement('button', 'articles-filter-toggle-mobile', 'Show Filters');
      mobileFilterToggleButton.type = 'button';
      mobileFilterToggleButton.setAttribute('aria-expanded', 'false');
      mobileFilterToggleButton.addEventListener('click', () => {
        const isOpen = sidebar.classList.toggle('articles-sidebar--open');
        mobileFilterToggleButton.textContent = isOpen ? 'Hide Filters' : 'Show Filters';
        mobileFilterToggleButton.setAttribute('aria-expanded', String(isOpen));
      });
      mainContent.appendChild(mobileFilterToggleButton);
    }

    const sortContainer = createElement('div', 'articles-sort-container');
    initSortBy(sortContainer);
    mainContent.appendChild(sortContainer);

    const resultsContainer = createElement('div', 'ais-Hits-list-articles');
    resultsContainer.dataset.articlesResults = '';
    mainContent.appendChild(resultsContainer);

    paginationContainer = createElement('div', 'articles-pagination-container');
    mainContent.appendChild(paginationContainer);

    panel.appendChild(wrapper);
  }

  function renderFacets(facets) {
    if (!sidebar || !facets) return;

    sidebar.innerHTML = '';
    
    const filtersHeader = createElement('div', 'articles-filters-header');
    filtersHeader.appendChild(createElement('h4', 'articles-filters-title', 'Filters'));
    filtersHeader.setAttribute('role', 'button');
    filtersHeader.setAttribute('aria-expanded', sidebar.classList.contains('articles-sidebar--open') ? 'true' : 'false');
    filtersHeader.addEventListener('click', () => {
      const isOpen = sidebar.classList.toggle('articles-sidebar--open');
      filtersHeader.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (mobileFilterToggleButton && !isCustomBlogContext) {
        mobileFilterToggleButton.textContent = isOpen ? 'Hide Filters' : 'Show Filters';
        mobileFilterToggleButton.setAttribute('aria-expanded', String(isOpen));
      }
    });
    
    if (Object.keys(selectedFacets).length > 0) {
      renderClearAllButton(filtersHeader);
    }
    
    sidebar.appendChild(filtersHeader);
    
    if (Object.keys(selectedFacets).length > 0) {
      renderSelectedFilters();
    }
    
    renderFacetOptions(facets);
  }

  function renderClearAllButton(headerWrapper) {
    const btn = createElement('button', 'articles-clear-all-btn', 'Clear All');
    btn.onclick = (e) => {
      e.preventDefault();
      resetFiltersAndPage();
      displayArticles();
    };
    headerWrapper.appendChild(btn);
  }

  function buildFilterItem(facetName, value) {
    const listItem = document.createElement('li');
    listItem.className = 'articles-filter-item';

    const link = document.createElement('a');
    link.href = '#';
    link.className = 'articles-filter-link';
    link.dataset.facet = facetName;
    link.dataset.value = value;

    const contentDiv = createElement('div', 'articles-filter-content');
    const selectedFilterLabel = (facetDisplayNames[facetName] || facetName).replace(/^By\s+/i, '').trim();
    contentDiv.textContent = `${selectedFilterLabel} : ${String(value).trim()}`;

    link.appendChild(contentDiv);

    const removeBtn = createElement('button', 'articles-filter-remove', '✕');
    removeBtn.onclick = (e) => {
      e.preventDefault();
      toggleFacet(facetName, value);
    };
    link.appendChild(removeBtn);

    listItem.appendChild(link);
    return listItem;
  }

  function renderSelectedFilters() {
    const container = createElement('div', 'articles-selected-filters');
    const filterList = document.createElement('ul');
    filterList.className = 'articles-filter-list';

    Object.keys(selectedFacets).forEach(facetName => {
      selectedFacets[facetName].forEach(value => {
        filterList.appendChild(buildFilterItem(facetName, value));
      });
    });

    container.appendChild(filterList);
    sidebar.appendChild(container);
  }

  function buildFacetOption(facetName, facetValue, count) {
    const isSelected = selectedFacets[facetName]?.includes(facetValue) || false;

    const label = createElement('label', 'articles-facet-label');

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'articles-facet-checkbox';
    checkbox.value = facetValue;
    checkbox.dataset.facet = facetName;
    checkbox.checked = isSelected;

    checkbox.onchange = () => {
      if (!selectedFacets[facetName]) {
        selectedFacets[facetName] = [];
      }
      if (checkbox.checked) {
        if (!selectedFacets[facetName].includes(facetValue)) {
          selectedFacets[facetName].push(facetValue);
        }
      } else {
        selectedFacets[facetName] = selectedFacets[facetName].filter(v => v !== facetValue);
        if (selectedFacets[facetName].length === 0) {
          delete selectedFacets[facetName];
        }
      }
      displayArticles();
    };

    label.appendChild(checkbox);

    const labelTextContainer = createElement('div', 'articles-facet-label-text');
    const facetValueSpan = createElement('span', 'articles-facet-value', facetValue);
    const countSpan = createElement('span', 'articles-facet-count', count);

    labelTextContainer.appendChild(facetValueSpan);
    labelTextContainer.appendChild(countSpan);

    labelTextContainer.onclick = (e) => {
      e.stopPropagation();
      if (!selectedFacets[facetName]) {
        selectedFacets[facetName] = [];
      }
      const isCurrentlySelected = selectedFacets[facetName].includes(facetValue);
      if (isCurrentlySelected) {
        selectedFacets[facetName] = selectedFacets[facetName].filter(v => v !== facetValue);
        if (selectedFacets[facetName].length === 0) {
          delete selectedFacets[facetName];
        }
      } else {
        selectedFacets[facetName].push(facetValue);
      }
      displayArticles();
    };
    label.appendChild(labelTextContainer);

    return label;
  }

  function renderFacetOptions(facets) {
    const facetOrder = ['meta.custom.by_concern', 'meta.custom.by_category'];
    const facetsToRender = facetOrder.filter(facetName => config.facets.includes(facetName) && facets[facetName]);

    facetsToRender.forEach(facetName => {
      const group = createElement('div', 'articles-facet-group');
      const displayName = facetDisplayNames[facetName] || facetName;

      const groupHeader = createElement('div', 'articles-facet-group-header');

      const toggleBtn = createElement('button', 'articles-facet-toggle');
      toggleBtn.setAttribute('aria-expanded', 'true');
      toggleBtn.setAttribute('aria-label', `Toggle ${displayName}`);

      const title = createElement('h5', 'articles-facet-title', displayName);
      toggleBtn.appendChild(title);

      const optionsWrapper = createElement('div', 'articles-facet-options');

      toggleBtn.onclick = (e) => {
        e.preventDefault();
        const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
        toggleBtn.setAttribute('aria-expanded', !isExpanded);
        optionsWrapper.classList.toggle('collapsed');
      };

      groupHeader.appendChild(toggleBtn);
      group.appendChild(groupHeader);

      Object.keys(facets[facetName]).sort((a, b) => a.localeCompare(b)).forEach(facetValue => {
        const count = facets[facetName][facetValue];
        optionsWrapper.appendChild(buildFacetOption(facetName, facetValue, count));
      });

      group.appendChild(optionsWrapper);
      sidebar.appendChild(group);
    });
  }

  function initSearchInput(controlsContainer) {
    const searchContainer = createElement('div', 'ais-search-box-container articles-search-container');
    searchContainer.innerHTML = `
      <div class="ais-search-box">
        <form novalidate class="ais-search-box--form">
          <input class="ais-search-box--input articles-search-input" type="search" placeholder="Search for articles" autocomplete="off" spellcheck="false">
          <button class="ais-SearchBox-submit articles-search-submit" type="submit" title="Submit the search query">
            <svg class="ais-SearchBox-submitIcon" width="10" height="10" viewBox="0 0 40 40" aria-hidden="true">
              <path d="M26.804 29.01c-2.832 2.34-6.465 3.746-10.426 3.746C7.333 32.756 0 25.424 0 16.378 0 7.333 7.333 0 16.378 0c9.046 0 16.378 7.333 16.378 16.378 0 3.96-1.406 7.594-3.746 10.426l10.534 10.534c.607.607.61 1.59-.004 2.202-.61.61-1.597.61-2.202.004L26.804 29.01zm-10.426.627c7.323 0 13.26-5.936 13.26-13.26 0-7.32-5.937-13.257-13.26-13.257C9.056 3.12 3.12 9.056 3.12 16.378c0 7.323 5.936 13.26 13.258 13.26z"></path>
            </svg>
          </button>
        </form>
      </div>
    `;
    controlsContainer.appendChild(searchContainer);

    const searchForm = searchContainer.querySelector('.ais-search-box--form');
    searchInput = searchContainer.querySelector('.ais-search-box--input');

    searchForm.addEventListener('submit', (event) => {
      event.preventDefault();
      displayArticles();
    });

    searchInput.addEventListener('input', () => {
      displayArticles();
    });
  }

  function initSortBy(controlsContainer) {
    const sortContainer = createElement('div', 'articles-sort-container');
    const sortList = createElement('div', 'articles-sort-list');
    
    sortList.appendChild(createElement('label', 'articles-sort-label', 'Sort By'));

    const selectWrapper = createElement('div', 'articles-sort-select-wrapper');
    const sortSelect = document.createElement('select');
    sortSelect.id = 'articles-sort-by';
    sortSelect.className = 'articles-sort-select';
    sortSelect.setAttribute('aria-label', 'Sort By');
    sortSelect.innerHTML = `
      <option value="featured">Featured</option>
      <option value="a_z">A to Z</option>
      <option value="z_a">Z to A</option>
    `;
    sortSelect.onchange = (e) => {
      currentSortBy = e.target.value;
      updateAlgoliaIndex(config.indices[currentSortBy]);
      displayArticles();
    };
    
    selectWrapper.appendChild(sortSelect);
    sortList.appendChild(selectWrapper);

    const resultCount = createElement('p', 'articles-results-count', '0-0 of 0 Articles');
    resultsCountEl = resultCount;

    sortContainer.appendChild(resultCount);
    sortContainer.appendChild(sortList);
    controlsContainer.appendChild(sortContainer);
  }

  async function displayArticles(page = 0) {
    if (!articlesIndex || !mainContent) return;

    const requestId = ++latestDisplayRequestId;

    try {
      const query = searchInput?.value || '';
      const facetFilters = buildFacetFilters();
      const hasActiveFacetFilters = facetFilters.length > 0;

      const articlePromise = articlesIndex.search(query, {
        page: 0,
        hitsPerPage: 1000,
        facets: config.facets,
        facetFilters: hasActiveFacetFilters ? facetFilters : undefined
      });

      const shouldFetchPages = shouldUsePagesIndex && !hasActiveFacetFilters && !!pagesIndex;

      const pagePromise = shouldFetchPages
        ? pagesIndex.search(query, {
            page: 0,
            hitsPerPage: 1000
          })
        : Promise.resolve({ hits: [] });

      const [articleResults, pageResults] = await Promise.all([articlePromise, pagePromise]);

      if (requestId !== latestDisplayRequestId) {
        return;
      }

      let combinedHits = [];

      if (articleResults.hits) {
        combinedHits = combinedHits.concat(
          articleResults.hits.map(hit => ({ ...hit, type: isPageLikeHit(hit) ? 'page' : 'article' }))
        );
      }

      if (shouldFetchPages && pageResults.hits) {
        combinedHits = combinedHits.concat(
          pageResults.hits.map(hit => ({ ...hit, type: 'page' }))
        );
      }

      if (hasActiveFacetFilters) {
        combinedHits = combinedHits.filter((hit) => hit.type !== 'page' && !isPageLikeHit(hit));
      }

      const totalHits = combinedHits.length;
      const totalPages = Math.ceil(totalHits / ITEMS_PER_PAGE);
      const startIdx = page * ITEMS_PER_PAGE;
      const endIdx = startIdx + ITEMS_PER_PAGE;
      const paginatedHits = combinedHits.slice(startIdx, endIdx);

      renderFacets(articleResults.facets);
      renderResults(paginatedHits, page, totalPages, totalHits);
    } catch (error) {
      if (requestId !== latestDisplayRequestId) {
        return;
      }
      console.error('Error displaying articles:', error);
    }
  }

  function renderResults(articles, currentPageNum = 0, totalPages = 1, totalHits = 0) {
    const container = mainContent.querySelector('[data-articles-results]');
    if (!container) return;

    if (resultsCountEl) {
      const start = totalHits > 0 ? (currentPageNum * ITEMS_PER_PAGE) + 1 : 0;
      const end = totalHits > 0 ? Math.min(start + articles.length - 1, totalHits) : 0;
      resultsCountEl.textContent = `${start}-${end} of ${totalHits} Articles`;
    }

    if (!articles.length) {
      container.innerHTML = '<p class="articles-no-results">No articles found</p>';
      return;
    }

    renderArticleCards(articles, container);
    renderPagination(paginationContainer, currentPageNum, totalPages);
  }

  function resolveArticleDescription(article) {
    const nestedShortDescription = article?.meta?.custom?.short_description;
    const shortDescriptionSourceValue = nestedShortDescription || article.short_description;

    if (shortDescriptionSourceValue) {
      try {
        return (typeof shortDescriptionSourceValue === 'string' && shortDescriptionSourceValue.startsWith('{'))
          ? extractTextFromRichText(JSON.parse(shortDescriptionSourceValue))
          : shortDescriptionSourceValue;
      } catch (e) {
        console.warn('[Algolia] Failed to parse rich text description:', e);
        return shortDescriptionSourceValue;
      }
    }

    if (article.body_html) {
      return cleanDescriptionText(article.body_html.replaceAll(/<[^>]{0,1000}>/g, '').trim());
    }

    if (article.content) {
      return cleanDescriptionText(article.content.replaceAll(/<[^>]{0,1000}>/g, '').trim());
    }

    return '';
  }

  function renderArticleCards(articles, container) {
    container.innerHTML = articles.map((article) => {
      const isPage = article.type === 'page';
      const descriptionText = resolveArticleDescription(article);
      const trimmedBody = getFirst3Lines(descriptionText, 95);

      let imageUrl = '';
      if (isPage && article.meta?.custom?.hero_banner) {
        imageUrl = article.meta.custom.hero_banner;
      } else {
        imageUrl = typeof article.image === 'string' ? article.image : article.image?.url || '';
      }
      const imageHtml = imageUrl ? `<img src="${imageUrl}" alt="${article.title}">` : '';

      const pageUrl = isPage
        ? `/pages/${article.handle}`
        : `/blogs/${config.blogHandle}/${article.handle}`;

      return `
        <div class="articles-card ${isPage ? 'articles-card--page' : 'articles-card--article'}">
          <div class="article-image-container">
            ${imageHtml ? `<a href="${pageUrl}" class="article-image-link" aria-label="${article.title}">${imageHtml}</a>` : ''}
            <a href="${pageUrl}">
              <h2 class="article-heading">${article.title}</h2>
            </a>
          </div>
          <div class="article-content">
            <p class="article-description">${trimmedBody}</p>
            <a href="${pageUrl}" class="article-readmore-block">Read More</a>
          </div>
        </div>
      `;
    }).join('');

    equalizeHeadingHeights(container);
  }

  function getFirst3Lines(description, maxChars = 95) {
    if (!description) {
      return '';
    }

    if (description.length <= maxChars) {
      return description;
    }

    let words = description.slice(0, maxChars);

    if (description[maxChars] !== ' ') {
      const lastSpaceIndex = words.lastIndexOf(' ');
      if (lastSpaceIndex > -1) {
        words = words.slice(0, lastSpaceIndex);
      }
    }

    return words + '...';
  }

  function cleanDescriptionText(text) {
    if (!text) {
      return '';
    }

    return String(text)
      .replaceAll(/\[\[[^\]]{0,1000}\]\]/g, '')
      .replaceAll(/\s{2,}/g, ' ')
      .trim();
  }
  
  function renderTrendingArticleCards(articles, container) {
    container.innerHTML = articles.map((article) => {
      const isPage = article.type === 'page';
      const descriptionText = resolveArticleDescription(article);
      const trimmedBody = getFirst3Lines(descriptionText, 95);

      let imageUrl = '';
      if (isPage && article.meta?.custom?.hero_banner) {
        imageUrl = article.meta.custom.hero_banner;
      } else {
        imageUrl = typeof article.image === 'string' ? article.image : article.image?.url || '';
      }
      const imageHtml = imageUrl ? `<img src="${imageUrl}" alt="${article.title}">` : '';

      const pageUrl = isPage
        ? `/pages/${article.handle}`
        : `/blogs/${config.blogHandle}/${article.handle}`;

      return `
        <li class="slick-slide" style="list-style-type: none;">
          <div class="articles-card ${isPage ? 'articles-card--page' : 'articles-card--article'}">
            <div class="article-image-container">
              ${imageHtml ? `<a href="${pageUrl}" class="article-image-link" aria-label="${article.title}">${imageHtml}</a>` : ''}
              <a href="${pageUrl}">
                <h2 class="article-heading">${article.title}</h2>
              </a>
            </div>
            <div class="article-content">
              <p class="article-description">${trimmedBody}</p>
              <a href="${pageUrl}" class="article-readmore-block">Read More</a>
            </div>
          </div>
        </li>
      `;
    }).join('');

    equalizeHeadingHeights(container);
  }

  function extractTextFromRichText(obj) {
    let text = '';
    if (typeof obj !== 'object' || obj === null) {
      return '';
    }
    if (obj.value && typeof obj.value === 'string') {
      return obj.value;
    }
    if (Array.isArray(obj.children)) {
      obj.children.forEach(child => {
        text += extractTextFromRichText(child);
      });
    }
    return text.trim();
  }

  const _equalizeResizeObservers = new WeakMap();

  function _runEqualizeHeadingHeights(container) {
    const cards = Array.from(container.querySelectorAll('.articles-card'));
    if (!cards.length) return;
    cards.forEach(card => { const h = card.querySelector('.article-heading'); if (h) h.style.height = ''; });
    const rowMap = new Map();
    cards.forEach(card => {
      const heading = card.querySelector('.article-heading');
      if (!heading) return;
      const rowKey = Math.round(card.getBoundingClientRect().top);
      if (!rowMap.has(rowKey)) rowMap.set(rowKey, []);
      rowMap.get(rowKey).push(heading);
    });
    rowMap.forEach(headings => {
      const max = Math.max(...headings.map(h => h.offsetHeight));
      headings.forEach(h => { h.style.height = max + 'px'; });
    });
  }

  function _doEqualizeHeadingFrame(container) {
    _runEqualizeHeadingHeights(container);
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => _runEqualizeHeadingHeights(container));
      observer.observe(container);
      _equalizeResizeObservers.set(container, observer);
    }
  }

  function equalizeHeadingHeights(container) {
    const existingObserver = _equalizeResizeObservers.get(container);
    if (existingObserver) {
      existingObserver.disconnect();
      _equalizeResizeObservers.delete(container);
    }
    requestAnimationFrame(() => requestAnimationFrame(() => _doEqualizeHeadingFrame(container)));
  }

  function createPaginationItem(label, pageNum, isSelected, isDisabled, onClick) {
    const item = document.createElement('li');
    item.className = 'ais-Pagination-item';
    
    if (isSelected) item.classList.add('ais-Pagination-item--selected');
    if (isDisabled) item.classList.add('ais-Pagination-item--disabled');
    
    const link = document.createElement('a');
    link.className = 'ais-Pagination-link';
    if (isSelected) link.classList.add('ais-Pagination-link--selected');
    
    link.href = '#';
    link.textContent = label;
    link.onclick = (e) => {
      e.preventDefault();
      if (!isDisabled) onClick();
    };
    
    item.appendChild(link);
    return item;
  }

  function renderPagination(container, currentPageNum, totalPages) {
    container.innerHTML = '';
    const list = document.createElement('ul');
    list.className = 'ais-Pagination-list';

    list.appendChild(createPaginationItem('<', null, false, currentPageNum === 0, () => displayArticles(currentPageNum - 1)));
    for (let i = 0; i < totalPages; i++) {
      list.appendChild(createPaginationItem(i + 1, i, i === currentPageNum, false, () => displayArticles(i)));
    }
    list.appendChild(createPaginationItem('>', null, false, currentPageNum >= totalPages - 1, () => displayArticles(currentPageNum + 1)));

    container.appendChild(list);
    const isTypingInSearch = !!searchInput && document.activeElement === searchInput;
    if (!isTypingInSearch) {
      mainContent.scrollIntoView({ behavior: 'smooth' });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  globalThis.articlesDebug = () => ({
    config,
    initialized,
    hasArticlesIndex: !!articlesIndex,
    hasPagesIndex: !!pagesIndex,
    selectedFacets
  });
})();